import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { GraduationCap, Award } from "lucide-react";
import { AskAboutButton } from "./AskAboutButton";

const FORMATIONS = [
  {
    id: 1,
    type: "Diplôme",
    degree: "Master Informatique",
    speciality: "Architecture logicielle & Cloud",
    school: "Université Paris-Saclay",
    year: "2017 — 2019",
    icon: GraduationCap,
    color: "#22d3ee",
    highlight: true,
  },
  {
    id: 2,
    type: "Diplôme",
    degree: "Licence Informatique",
    speciality: "Développement web & Systèmes",
    school: "IUT Paris Descartes",
    year: "2015 — 2017",
    icon: GraduationCap,
    color: "#a78bfa",
    highlight: false,
  },
  {
    id: 3,
    type: "Certification",
    degree: "AWS Certified Developer",
    speciality: "Associate — Amazon Web Services",
    school: "Amazon",
    year: "2022",
    icon: Award,
    color: "#f97316",
    highlight: false,
  },
  {
    id: 4,
    type: "Certification",
    degree: "Professional Scrum Master I",
    speciality: "Agile & Scrum Framework",
    school: "Scrum.org",
    year: "2021",
    icon: Award,
    color: "#86efac",
    highlight: false,
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export function Education({ education = [], language = "fr" }: { education?: any[], language?: string }) {
  const { ref, visible } = useInView(0.05);
  const [hovered, setHovered] = useState<number | null>(null);

  const displayEducation = education.length > 0
    ? education.map((edu: any, index: number) => {
      const startYear = new Date(edu.start_date).getFullYear();
      const endYear = edu.end_date ? new Date(edu.end_date).getFullYear() : (language === "en" ? "Present" : "Présent");
      return {
        id: edu.id || index,
        type: "Diplôme",
        degree: edu.degree,
        speciality: edu.speciality || edu.description || "",
        school: edu.school || edu.institution,
        year: `${startYear} — ${endYear}`,
        icon: GraduationCap,
        color: index % 2 === 0 ? "#22d3ee" : "#a78bfa",
        highlight: index === 0,
      };
    })
    : FORMATIONS;

  return (
    <section
      id="education"
      ref={ref}
      className="py-24 md:py-36 relative"
      style={{ background: "#050508" }}
    >
      <div
        className="absolute right-1/4 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: "400px",
          height: "400px",
          background: "radial-gradient(circle, rgba(167,139,250,0.04) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={visible ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-16"
        >
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
              color: "#22d3ee",
              letterSpacing: "0.15em",
            }}
          >
            {language === "en" ? "04 // EDUCATION" : "04 // FORMATIONS"}
          </span>
          <div className="flex-1 h-px" style={{ background: "rgba(34,211,238,0.15)" }} />
        </motion.div>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1, duration: 0.6 }}
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "clamp(32px, 4vw, 52px)",
              fontWeight: 700,
              color: "#ffffff",
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {language === "en" ? "Degrees & " : "Diplômes & "}
            <span style={{ color: "#a78bfa" }}>{language === "en" ? "certifications" : "certifications"}</span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {displayEducation.map((item, i) => {
            const Icon = item.icon;
            const isHov = hovered === item.id;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                animate={visible ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 + i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                onMouseEnter={() => setHovered(item.id)}
                onMouseLeave={() => setHovered(null)}
                className="relative group p-6 rounded-2xl transition-all"
                style={{
                  background: isHov ? `${item.color}08` : "rgba(255,255,255,0.025)",
                  border: `1px solid ${isHov ? item.color + "30" : "rgba(255,255,255,0.06)"}`,
                  cursor: "none",
                  transition: "all 0.3s ease",
                }}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div
                    className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl"
                    style={{
                      background: `${item.color}15`,
                      border: `1px solid ${item.color}30`,
                    }}
                  >
                    <Icon size={18} color={item.color} />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Type badge */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span
                        className="px-2 py-0.5 rounded-full"
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: "9px",
                          letterSpacing: "0.1em",
                          color: item.color,
                          background: `${item.color}15`,
                          border: `1px solid ${item.color}25`,
                        }}
                      >
                        {item.type.toUpperCase()}
                      </span>
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: "11px",
                          color: "rgba(255,255,255,0.3)",
                        }}
                      >
                        {item.year}
                      </span>
                    </div>

                    {/* Degree */}
                    <h3
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "17px",
                        fontWeight: 700,
                        color: isHov ? "#ffffff" : "rgba(255,255,255,0.9)",
                        transition: "color 0.2s",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {item.degree}
                    </h3>

                    {/* Speciality */}
                    <p
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "13px",
                        color: "rgba(255,255,255,0.4)",
                        marginTop: "2px",
                      }}
                    >
                      {item.speciality}
                    </p>

                    {/* School */}
                    <p
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "13px",
                        color: item.color,
                        marginTop: "6px",
                        opacity: 0.8,
                      }}
                    >
                      {item.school}
                    </p>
                  </div>
                </div>

                {/* Ask about this — absolutely positioned */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "16px",
                    right: "16px",
                    opacity: isHov ? 1 : 0,
                    transition: "opacity 0.25s ease",
                    pointerEvents: isHov ? "auto" : "none",
                  }}
                >
                  <AskAboutButton
                    subject={`${item.degree} @ ${item.school}`}
                    language={language}
                    color={item.color}
                    section="education"
                    context={`Diplôme: ${item.degree}\nÉtablissement: ${item.school}\nSpécialité: ${item.speciality}\nPériode: ${item.year}`}
                  />
                </div>

                {/* Highlight indicator */}
                {item.highlight && (
                  <div
                    className="absolute top-0 left-0 w-full h-0.5 rounded-t-2xl"
                    style={{
                      background: `linear-gradient(90deg, ${item.color}, transparent)`,
                    }}
                  />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
